import { useCallback, useEffect, useState } from 'react';

import {
  deleteUserAccount,
  getUser,
  listUsers,
  type ListUsersParams,
  type UserDetail,
  type UserListItem,
} from '../../lib/panelApi';

import { UserDetailDrawer } from './UserDetailDrawer';

type Props = {
  accessToken: string;
};

type Filters = Required<Omit<ListUsersParams, 'q' | 'limit' | 'offset'>> & {
  q: string;
  limit: number;
  offset: number;
};

const DEFAULT_FILTERS: Filters = {
  q: '',
  status: 'active',
  sort: 'created_at',
  order: 'desc',
  limit: 50,
  offset: 0,
};

/**
 * Users management screen.
 *
 * Capabilities:
 *   • Filter by lifecycle status (active / deleted / all).
 *   • Sort by created_at / last_active_at / last_login_at.
 *   • Search across email / name / google_id.
 *   • Click a row → side drawer with full profile, library
 *     statistics and a moderation action ("Delete account").
 */
export function UsersPage({ accessToken }: Props) {
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<UserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Drawer state.
  const [selectedUUID, setSelectedUUID] = useState<string | null>(null);
  const [selected, setSelected] = useState<UserDetail | null>(null);
  const [selectedLoading, setSelectedLoading] = useState(false);

  const refresh = useCallback(
    async (overrides?: Partial<Filters>) => {
      const next = { ...filters, ...(overrides ?? {}) };
      setFilters(next);
      setLoading(true);
      setError(null);
      try {
        const res = await listUsers(accessToken, {
          q: next.q,
          status: next.status,
          sort: next.sort,
          order: next.order,
          limit: next.limit,
          offset: next.offset,
        });
        setItems(res.items);
        setTotal(res.total);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load users');
      } finally {
        setLoading(false);
      }
    },
    [accessToken, filters],
  );

  useEffect(() => {
    void refresh({ offset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // Load the detail panel whenever the selection changes.
  useEffect(() => {
    if (!selectedUUID) {
      setSelected(null);
      return;
    }
    let cancelled = false;
    setSelected(null);
    setSelectedLoading(true);
    getUser(accessToken, selectedUUID)
      .then((d) => {
        if (!cancelled) setSelected(d);
      })
      .catch(() => {
        if (!cancelled) setSelected(null);
      })
      .finally(() => {
        if (!cancelled) setSelectedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, selectedUUID]);

  const onDelete = async (reason: string) => {
    if (!selected) return;
    await deleteUserAccount(accessToken, selected.uuid, reason);
    setSelectedUUID(null);
    await refresh();
  };

  const onPage = (delta: number) => {
    const next = Math.max(0, filters.offset + delta * filters.limit);
    void refresh({ offset: next });
  };

  return (
    <div className="card">
      <div className="header" style={{ marginBottom: 8 }}>
        <div>
          <div className="sectionTitle" style={{ margin: 0 }}>
            Users
          </div>
          <div className="subtitle">
            App users (Google Sign-In). Click a row to open the profile drawer.
          </div>
        </div>
        <button className="btn" onClick={() => refresh()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div className="row" style={{ marginBottom: 10, alignItems: 'end', gap: 8, flexWrap: 'wrap' }}>
        <div className="field" style={{ marginTop: 0, flex: '1 1 260px', minWidth: 220 }}>
          <div className="label">Search</div>
          <input
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void refresh({ offset: 0 });
            }}
            placeholder="Search by email, name or google_id…"
          />
        </div>

        <div className="field" style={{ marginTop: 0 }}>
          <div className="label">Status</div>
          <select
            value={filters.status}
            onChange={(e) => void refresh({ status: e.target.value as Filters['status'], offset: 0 })}
          >
            <option value="active">Active</option>
            <option value="deleted">Deleted</option>
            <option value="all">All</option>
          </select>
        </div>

        <div className="field" style={{ marginTop: 0 }}>
          <div className="label">Sort</div>
          <select
            value={filters.sort}
            onChange={(e) => void refresh({ sort: e.target.value as Filters['sort'], offset: 0 })}
          >
            <option value="created_at">Joined</option>
            <option value="last_active_at">Last active</option>
            <option value="last_login_at">Last login</option>
          </select>
        </div>

        <div className="field" style={{ marginTop: 0 }}>
          <div className="label">Order</div>
          <select
            value={filters.order}
            onChange={(e) => void refresh({ order: e.target.value as Filters['order'], offset: 0 })}
          >
            <option value="desc">Newest first</option>
            <option value="asc">Oldest first</option>
          </select>
        </div>

        <button className="btn btn-secondary" onClick={() => refresh({ offset: 0 })} disabled={loading}>
          Search
        </button>
        <button
          className="btn"
          onClick={() => void refresh({ ...DEFAULT_FILTERS })}
          disabled={loading}
        >
          Reset
        </button>
      </div>

      {error ? (
        <div style={{ marginBottom: 10 }}>
          <div className="errorBox">{error}</div>
        </div>
      ) : null}

      {loading ? (
        <div className="muted">Loading users…</div>
      ) : items.length === 0 ? (
        <div className="muted">No users found.</div>
      ) : (
        <>
          <div className="list">
            {items.map((u) => (
              <UserRow
                key={u.uuid}
                user={u}
                active={u.uuid === selectedUUID}
                onSelect={() => setSelectedUUID(u.uuid)}
              />
            ))}
          </div>

          <div
            className="row"
            style={{ marginTop: 12, alignItems: 'center', gap: 12, justifyContent: 'space-between' }}
          >
            <div className="muted" style={{ fontSize: 12 }}>
              Showing {filters.offset + 1}–{Math.min(filters.offset + items.length, total)} of {total}
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn"
                disabled={filters.offset === 0 || loading}
                onClick={() => onPage(-1)}
              >
                ← Prev
              </button>
              <button
                className="btn"
                disabled={filters.offset + items.length >= total || loading}
                onClick={() => onPage(1)}
              >
                Next →
              </button>
            </div>
          </div>
        </>
      )}

      <UserDetailDrawer
        open={selectedUUID != null}
        loading={selectedLoading}
        user={selected}
        onClose={() => setSelectedUUID(null)}
        onDelete={onDelete}
      />
    </div>
  );
}

function UserRow({
  user,
  active,
  onSelect,
}: {
  user: UserListItem;
  active: boolean;
  onSelect: () => void;
}) {
  const isDeleted = !!user.deleted_at;
  const lastActive = user.last_active_at ?? user.last_login_at ?? user.created_at;
  return (
    <button
      type="button"
      className="item"
      onClick={onSelect}
      style={{
        textAlign: 'left',
        cursor: 'pointer',
        background: active ? 'rgba(10,25,47,0.06)' : undefined,
        borderColor: active ? 'var(--accent, #0a192f)' : undefined,
        width: '100%',
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <Avatar src={user.picture_url} name={user.name || user.email} size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 850, opacity: isDeleted ? 0.5 : 1 }}>
            {user.name || user.email}
            {isDeleted ? <span className="pill pill-bad" style={{ marginLeft: 8 }}>Deleted</span> : null}
          </div>
          <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
            {user.email} • Joined {formatDate(user.created_at)} • Last active {formatRelative(lastActive)}
          </div>
        </div>
        <div className="row" style={{ gap: 8, flexShrink: 0 }}>
          <Stat label="Library" value={user.book_count} />
          <Stat label="Finished" value={user.finished_count} />
        </div>
      </div>
    </button>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="pill" style={{ minWidth: 70, justifyContent: 'center' }}>
      <span className="muted" style={{ fontSize: 11 }}>{label}</span>
      <span style={{ fontWeight: 900 }}>{value}</span>
    </div>
  );
}

function Avatar({ src, name, size }: { src?: string; name: string; size: number }) {
  if (src) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        style={{ borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
        referrerPolicy="no-referrer"
      />
    );
  }
  const initial = name.trim().slice(0, 1).toUpperCase() || '?';
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'var(--accent, #0a192f)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 900,
        flexShrink: 0,
      }}
    >
      {initial}
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function formatRelative(iso?: string): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 30) return `${Math.round(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}
